import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';
import { captureSentryException } from './sentry';
import { app } from './firebaseApp';

/** Attest requests to Firebase when reCAPTCHA v3 site key is set and App Check enforcement is enabled in Firebase console. */
const recaptchaSiteKey = import.meta.env.VITE_FIREBASE_RECAPTCHA_SITE_KEY;
if (
  typeof window !== 'undefined' &&
  recaptchaSiteKey &&
  (import.meta.env.PROD || import.meta.env.VITE_FIREBASE_APPCHECK_ENABLE_DEV === 'true')
) {
  const debugTok = import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN;
  if (import.meta.env.DEV && debugTok) {
    (window as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugTok === 'true' ? true : debugTok;
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

/** Memory-only cache avoids persistent-cache races that trigger SDK ca9/b815 assertion failures. */
export const db = initializeFirestore(
  app,
  { localCache: memoryLocalCache() },
  firebaseConfig.firestoreDatabaseId,
);
export const auth = getAuth(app);
export const storage = getStorage(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

/** Firestore rejects `undefined` field values; strip them before writes. */
export function stripUndefinedForFirestore<T>(value: T): T {
  if (value === undefined) {
    return value;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefinedForFirestore(item)) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (val === undefined) continue;
    out[key] = stripUndefinedForFirestore(val);
  }
  return out as T;
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string;
    email?: string | null;
    emailVerified?: boolean;
    isAnonymous?: boolean;
    tenantId?: string | null;
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  const errInfo: FirestoreErrorInfo = {
    error: errorMessage,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
    },
    operationType,
    path
  }
  
  if (errorMessage.includes("transport errored") || errorMessage.includes("WebChannelConnection") || errorMessage.includes("offline")) {
    console.warn('Firestore Transient Network Event: ', JSON.stringify(errInfo));
    return;
  }

  if (errorMessage.includes('requires an index')) {
    console.warn(
      'Firestore index missing — deploy firestore.indexes.json:',
      JSON.stringify(errInfo),
    );
    return;
  }

  if (
    !auth.currentUser &&
    (errorMessage.includes('permission-denied') ||
      errorMessage.includes('Missing or insufficient permissions'))
  ) {
    console.warn('Firestore permission denied (signed out):', JSON.stringify(errInfo));
    return;
  }

  console.error('Firestore Error: ', JSON.stringify(errInfo));
  captureSentryException(error instanceof Error ? error : new Error(errorMessage), {
    context: 'firestore',
    firestore: errInfo,
  });
  // Do not re-throw here — an async throw was crashing React and leaving a blank screen.
}
